import React, { useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Button, ConfirmDialog } from '../../components/ui';
import { registrationSettings } from '../../api/endpoints';
import type { RegistrationSettings } from '../../api/endpoints';
import type { Notify } from '../../hooks';
import type { Tokens } from '../../theme/tokens';

// The public family-registration link: switch it on or off, share it, or
// replace it. Saved through its own endpoint, not the Settings form, so the
// Save on any other section can never close the link or change its token.

interface Props {
  notify: Notify;
  t: Tokens;
  isDarkMode?: boolean;
}

/** The app's own address with the registration route, e.g. https://host/churchManag/register/<token>. */
export const registrationLink = (token: string) =>
  `${window.location.origin}${(process.env.PUBLIC_URL || '').replace(/\/+$/, '')}/register/${token}`;

const PublicRegistrationSection = ({ notify, t, isDarkMode }: Props) => {
  const [settings, setSettings] = useState<RegistrationSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const qrRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let active = true;
    registrationSettings.get()
      .then((s) => { if (active) setSettings(s); })
      .catch((err) => notify((err as Error).message || 'Could not load the registration settings.', 'error'));
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!settings) return <p style={{ color: t.textMuted }}>Loading…</p>;

  const link = registrationLink(settings.token);
  const shareText = 'Please register your family with the church using this form:';

  const toggle = async (enabled: boolean) => {
    setBusy(true);
    try {
      setSettings(await registrationSettings.setEnabled(enabled));
      notify(enabled ? 'Public registration is now ON.' : 'Public registration is now OFF. The link shows "not available".', 'success');
    } catch (err) {
      notify((err as Error).message || 'Could not change the setting.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    setConfirmRegenerate(false);
    setBusy(true);
    try {
      setSettings(await registrationSettings.regenerate());
      notify('New link created. The old link no longer works.', 'success');
    } catch (err) {
      notify((err as Error).message || 'Could not create a new link.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      notify('Link copied.', 'success');
    } catch {
      notify('Could not copy automatically — select the link and copy it.', 'error');
    }
  };

  /** The QR as a PNG, for a notice board, a bulletin or a WhatsApp image. */
  const downloadQr = () => {
    const canvas = qrRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = 'family-registration-qr.png';
    a.click();
  };

  const on = settings.enabled;

  return (
    <div style={{ maxWidth: '720px' }}>
      <p style={{ color: t.textMuted, fontSize: t.fontSize.md, marginTop: 0, lineHeight: 1.6 }}>
        Share one link with the whole congregation. Each family enters the head's mobile number and date of birth,
        fills in the head and every member, and submits — no login needed. Submissions wait under
        <strong> Members &amp; Families → Pending Approval</strong> until the office approves them.
        Families can check the status and edit their details until approval.
      </p>

      <label style={{
        display: 'flex', alignItems: 'center', gap: '12px', cursor: busy ? 'wait' : 'pointer',
        padding: '14px', borderRadius: t.radius.lg, border: `1px solid ${on ? t.success : t.border}`,
        backgroundColor: t.bgMain, marginBottom: '16px'
      }}>
        <input
          type="checkbox" checked={on} disabled={busy}
          onChange={(e) => toggle(e.target.checked)}
          style={{ width: '20px', height: '20px', cursor: 'pointer' }}
        />
        <span>
          <span style={{ fontWeight: 'bold', color: t.textMain, fontSize: t.fontSize.md }}>
            Public registration is {on ? 'ON' : 'OFF'}
          </span>
          <span style={{ display: 'block', color: t.textMuted, fontSize: t.fontSize.sm, marginTop: '3px' }}>
            {on
              ? 'Anyone with the link can register a family or check their status.'
              : 'Anyone opening the link sees "Registration is not available". Existing submissions are kept.'}
          </span>
        </span>
      </label>

      <div style={{ fontWeight: 'bold', color: t.textMain, fontSize: t.fontSize.md, marginBottom: '6px' }}>
        Registration link
      </div>
      <input
        readOnly value={link} onFocus={(e) => e.target.select()} aria-label="Registration link"
        style={{
          width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: t.radius.md,
          border: `1px solid ${t.border}`, backgroundColor: t.bgSubtle, color: on ? t.textMain : t.textMuted,
          fontFamily: 'monospace', fontSize: t.fontSize.sm
        }}
      />
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
        <Button variant="secondary" size="sm" isDarkMode={isDarkMode} onClick={copy}>📋 Copy link</Button>
        <Button
          variant="secondary" size="sm" isDarkMode={isDarkMode}
          onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${link}`)}`, '_blank', 'noopener')}
        >
          💬 Share on WhatsApp
        </Button>
        <Button variant="secondary" size="sm" isDarkMode={isDarkMode} onClick={() => window.open(link, '_blank', 'noopener')}>
          ↗ Open form
        </Button>
        <Button variant="danger" size="sm" disabled={busy} isDarkMode={isDarkMode} onClick={() => setConfirmRegenerate(true)}>
          🔄 Generate new link
        </Button>
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap', marginTop: '18px',
        padding: '14px', borderRadius: t.radius.lg, border: `1px solid ${t.border}`, backgroundColor: t.bgSubtle
      }}>
        {/* Always dark-on-white, whatever the theme, so every phone camera reads it. */}
        <div style={{ padding: '10px', backgroundColor: '#FFFFFF', borderRadius: t.radius.md, lineHeight: 0, opacity: on ? 1 : 0.4 }}>
          <QRCodeCanvas
            ref={qrRef} value={link} size={512} marginSize={2} level="M"
            style={{ width: '180px', height: '180px' }}
            aria-label="QR code for the registration link"
          />
        </div>
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <div style={{ fontWeight: 'bold', color: t.textMain, fontSize: t.fontSize.md, marginBottom: '4px' }}>QR code</div>
          <p style={{ color: t.textMuted, fontSize: t.fontSize.sm, margin: '0 0 10px', lineHeight: 1.5 }}>
            Families scan this with their phone camera to open the form. Print it on the notice board or
            bulletin, or show it on the screen after service. Generating a new link changes the QR code too.
          </p>
          <Button variant="secondary" size="sm" isDarkMode={isDarkMode} onClick={downloadQr}>⬇ Download QR code</Button>
        </div>
      </div>

      {!on && (
        <p style={{ color: t.warning, fontSize: t.fontSize.sm, marginTop: '10px' }}>
          The link is OFF — switch it on before sharing.
        </p>
      )}

      <ConfirmDialog
        open={confirmRegenerate}
        title="Generate a new link?"
        message="The current link stops working immediately for everyone who has it. Submissions already made are kept, and families can check them through the new link."
        confirmLabel="Generate new link"
        destructive
        onConfirm={regenerate}
        onCancel={() => setConfirmRegenerate(false)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default PublicRegistrationSection;
